import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MainNavigation } from '../main/MainNavigation';
import { ChefHat, Shield, Lock, Eye, Database, Users } from 'lucide-react';

export function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-menioo-cream">
      <MainNavigation />
      
      <div className="container mx-auto px-6 md:px-12 py-12">
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-8 md:p-12">
          <div className="flex items-center mb-4">
            <Shield className="h-10 w-10 text-menioo-green mr-4" />
            <h1 className="text-4xl font-display font-bold text-menioo-navy">
              Politique de Confidentialité
            </h1>
          </div>
          
          <p className="text-gray-600 mb-8">
            <strong>Dernière mise à jour : 13 février 2026</strong>
          </p>

          <div className="prose prose-lg max-w-none">
            {/* Introduction */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-menioo-navy mb-4">1. Introduction</h2>
              <p className="text-gray-700 mb-4">
                La présente politique de confidentialité décrit comment Menioo collecte, utilise, stocke et protège vos données personnelles conformément au Règlement Général sur la Protection des Données (RGPD - Règlement UE 2016/679) et à la loi française Informatique et Libertés.
              </p>
              <p className="text-gray-700 mb-4">
                Menioo est édité par <strong>Alexandre CALVET</strong>, entrepreneur individuel, joignable à l'adresse : <a href="mailto:alex@menioo.com" className="text-menioo-green hover:underline">alex@menioo.com</a>
              </p>
              <p className="text-gray-700">
                En utilisant notre application accessible à l'adresse <a href="https://app.menioo.com" className="text-menioo-green hover:underline">https://app.menioo.com</a>, notre application mobile, ou notre site institutionnel <a href="https://menioo.com" className="text-menioo-green hover:underline">https://menioo.com</a>, vous acceptez les pratiques décrites dans cette politique.
              </p>
            </section>

            {/* Responsable du traitement */}
            <section className="mb-8">
              <div className="flex items-start">
                <Users className="h-6 w-6 text-menioo-green mr-3 mt-1" />
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-menioo-navy mb-4">2. Responsable du traitement</h2>
                  <div className="bg-menioo-cream p-4 rounded-lg">
                    <ul className="list-none text-gray-700 space-y-2">
                      <li><strong>Nom :</strong> Alexandre CALVET</li>
                      <li><strong>Email :</strong> <a href="mailto:alex@menioo.com" className="text-menioo-green hover:underline">alex@menioo.com</a></li>
                      <li><strong>Site web :</strong> <a href="https://menioo.com" className="text-menioo-green hover:underline">https://menioo.com</a></li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Données collectées */}
            <section className="mb-8">
              <div className="flex items-start">
                <Database className="h-6 w-6 text-menioo-green mr-3 mt-1" />
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-menioo-navy mb-4">3. Données personnelles collectées</h2>
                  
                  <h3 className="text-xl font-semibold text-menioo-navy mb-3">3.1 Données fournies directement par l'utilisateur</h3>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                    <li><strong>Données d'inscription :</strong> adresse email, nom d'affichage, mot de passe (chiffré)</li>
                    <li><strong>Données de profil :</strong> préférences alimentaires, régimes spécifiques</li>
                    <li><strong>Contenu créé :</strong> recettes (publiques et privées), plans de repas, listes de courses</li>
                    <li><strong>Données d'organisation :</strong> nom de l'organisation, invitations envoyées par email</li>
                    <li><strong>Données de paiement :</strong> traitées exclusivement par notre prestataire Stripe. Nous ne stockons jamais vos numéros de carte bancaire.</li>
                  </ul>

                  <h3 className="text-xl font-semibold text-menioo-navy mb-3">3.2 Données collectées automatiquement</h3>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                    <li><strong>Données techniques :</strong> adresse IP, type de navigateur, système d'exploitation, type d'appareil</li>
                    <li><strong>Données d'utilisation :</strong> pages consultées, fonctionnalités utilisées, horodatages d'accès</li>
                    <li><strong>Cookies et technologies similaires :</strong> voir la section 8 ci-dessous</li>
                  </ul>

                  <h3 className="text-xl font-semibold text-menioo-navy mb-3">3.3 Données liées à l'importation de recettes</h3>
                  <p className="text-gray-700">
                    Lorsque vous utilisez notre fonctionnalité d'importation de recettes depuis TikTok ou d'autres sites web, nous collectons les URL que vous soumettez ainsi que les données extraites par notre moteur d'intelligence artificielle (ingrédients, instructions, attribution au créateur). Nous ne collectons pas vos identifiants de connexion à ces plateformes tierces.
                  </p>
                </div>
              </div>
            </section>

            {/* Finalités */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-menioo-navy mb-4">4. Finalités et bases légales du traitement</h2>
              <p className="text-gray-700 mb-3">Nous traitons vos données personnelles pour les finalités suivantes :</p>
              <div className="space-y-4">
                <div className="border-l-4 border-menioo-green pl-4">
                  <h4 className="font-semibold text-gray-900">Exécution du contrat (Art. 6.1.b RGPD)</h4>
                  <p className="text-gray-700">Fourniture des services Menioo, gestion de votre compte, gestion des abonnements et paiements, planification de repas et génération de listes de courses</p>
                </div>
                <div className="border-l-4 border-menioo-orange pl-4">
                  <h4 className="font-semibold text-gray-900">Intérêt légitime (Art. 6.1.f RGPD)</h4>
                  <p className="text-gray-700">Amélioration de nos services, analyse d'utilisation agrégée, prévention de la fraude et sécurité</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-semibold text-gray-900">Consentement (Art. 6.1.a RGPD)</h4>
                  <p className="text-gray-700">Envoi de communications marketing, affichage de publicités personnalisées (utilisateurs gratuits uniquement), utilisation de cookies non essentiels</p>
                </div>
                <div className="border-l-4 border-gray-500 pl-4">
                  <h4 className="font-semibold text-gray-900">Obligation légale (Art. 6.1.c RGPD)</h4>
                  <p className="text-gray-700">Conservation des données de facturation, réponse aux obligations fiscales et comptables</p>
                </div>
              </div>
            </section>

            {/* Partage des données */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-menioo-navy mb-4">5. Partage et transfert des données</h2>
              <p className="text-gray-700 mb-4">
                <strong>Nous ne vendons jamais vos données personnelles.</strong> Nous partageons vos données uniquement avec les prestataires suivants, nécessaires au fonctionnement du service :
              </p>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border border-gray-300 px-4 py-2 text-left">Prestataire</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Rôle</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Localisation</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Garanties</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">Firebase (Google Cloud)</td>
                      <td className="border border-gray-300 px-4 py-2">Hébergement, authentification, base de données</td>
                      <td className="border border-gray-300 px-4 py-2">États-Unis</td>
                      <td className="border border-gray-300 px-4 py-2">Clauses contractuelles types (CCT)</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">Stripe</td>
                      <td className="border border-gray-300 px-4 py-2">Traitement des paiements</td>
                      <td className="border border-gray-300 px-4 py-2">États-Unis</td>
                      <td className="border border-gray-300 px-4 py-2">CCT</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">Mailjet</td>
                      <td className="border border-gray-300 px-4 py-2">Emails transactionnels et marketing</td>
                      <td className="border border-gray-300 px-4 py-2">Europe</td>
                      <td className="border border-gray-300 px-4 py-2">Hébergement UE</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">Netlify</td>
                      <td className="border border-gray-300 px-4 py-2">Hébergement du site web</td>
                      <td className="border border-gray-300 px-4 py-2">États-Unis</td>
                      <td className="border border-gray-300 px-4 py-2">CCT</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Conservation */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-menioo-navy mb-4">6. Durée de conservation des données</h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Données de compte :</strong> conservées pendant toute la durée de votre inscription, puis supprimées dans un délai de 30 jours après la suppression de votre compte</li>
                <li><strong>Recettes publiques :</strong> conservées même après suppression du compte, sauf demande explicite de suppression</li>
                <li><strong>Données de paiement :</strong> conservées pendant la durée légale obligatoire (10 ans pour les données de facturation)</li>
                <li><strong>Données de navigation et cookies :</strong> conservées pendant 13 mois maximum</li>
                <li><strong>Données d'utilisation agrégées et anonymisées :</strong> conservées sans limitation de durée</li>
              </ul>
            </section>

            {/* Droits */}
            <section className="mb-8">
              <div className="flex items-start">
                <Eye className="h-6 w-6 text-menioo-green mr-3 mt-1" />
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-menioo-navy mb-4">7. Vos droits</h2>
                  <p className="text-gray-700 mb-4">
                    Conformément au RGPD et à la loi Informatique et Libertés, vous disposez des droits suivants :
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                    <li><strong>Droit d'accès :</strong> obtenir une copie de vos données personnelles</li>
                    <li><strong>Droit de rectification :</strong> corriger des données inexactes ou incomplètes</li>
                    <li><strong>Droit à l'effacement :</strong> demander la suppression de vos données (« droit à l'oubli »)</li>
                    <li><strong>Droit à la limitation du traitement :</strong> restreindre temporairement l'utilisation de vos données</li>
                    <li><strong>Droit à la portabilité :</strong> recevoir vos données dans un format structuré et lisible par machine</li>
                    <li><strong>Droit d'opposition :</strong> vous opposer au traitement de vos données à des fins de marketing</li>
                    <li><strong>Droit de retirer votre consentement :</strong> à tout moment, sans affecter la licéité du traitement antérieur</li>
                  </ul>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-gray-700">
                      <strong>Pour exercer vos droits :</strong> contactez-nous à l'adresse <a href="mailto:alex@menioo.com" className="text-menioo-green hover:underline font-medium">alex@menioo.com</a>. Nous répondrons dans un délai de 30 jours.
                    </p>
                    <p className="text-gray-700 mt-2">
                      Vous avez également le droit d'introduire une réclamation auprès de la <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-menioo-green hover:underline font-medium">CNIL</a> (Commission Nationale de l'Informatique et des Libertés).
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Cookies */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-menioo-navy mb-4">8. Cookies et technologies similaires</h2>
              <p className="text-gray-700 mb-3">Menioo utilise des cookies et technologies similaires pour améliorer votre expérience. Vous pouvez gérer vos préférences à tout moment via notre gestionnaire de consentement.</p>
              
              <div className="space-y-3 mb-4">
                <div className="bg-green-50 border-l-4 border-menioo-green p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🔒 Cookies essentiels (obligatoires)</h4>
                  <p className="text-gray-700 mb-2">Ces cookies sont nécessaires au fonctionnement du service et ne requièrent pas votre consentement :</p>
                  <ul className="list-disc pl-5 text-gray-700 text-sm space-y-1">
                    <li><strong>__session</strong> (Firebase) : Authentification et maintien de session</li>
                    <li><strong>menioo_tarteaucitron</strong> : Mémorisation de vos choix de cookies</li>
                    <li><strong>menioo_lang</strong> : Préférence de langue (fr/en)</li>
                  </ul>
                  <p className="text-xs text-gray-600 mt-2">Durée : Session ou 13 mois maximum</p>
                </div>
                
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">📊 Cookies analytiques (optionnels)</h4>
                  <p className="text-gray-700 mb-2">Mesure d'audience anonyme pour améliorer nos services. Soumis à votre consentement :</p>
                  <ul className="list-disc pl-5 text-gray-700 text-sm space-y-1">
                    <li><strong>_ga, _gid</strong> (Google Analytics) : Statistiques d'utilisation anonymes</li>
                  </ul>
                  <p className="text-xs text-gray-600 mt-2">Durée : 2 ans (_ga), 24 heures (_gid)</p>
                  <p className="text-xs text-gray-500 mt-1"><em>Note : Non activés actuellement</em></p>
                </div>
                
                <div className="bg-gray-50 border-l-4 border-gray-400 p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">📢 Cookies publicitaires</h4>
                  <p className="text-gray-700 text-sm">
                    <em>Aucun cookie publicitaire n'est actuellement utilisé sur Menioo.</em>
                  </p>
                </div>
              </div>

              <div className="bg-menioo-cream border border-menioo-green rounded-lg p-4">
                <p className="text-gray-700 font-medium mb-2">
                  💡 Gérer vos préférences de cookies
                </p>
                <p className="text-gray-600 text-sm mb-3">
                  Vous pouvez consulter et gérer vos préférences de cookies à tout moment. 
                  Cliquez sur le bouton ci-dessous ou sur l'icône cookies en bas à droite de votre écran.
                </p>
                <button 
                  onClick={() => {
                    if (typeof window !== 'undefined' && (window as any).tarteaucitron) {
                      (window as any).tarteaucitron.userInterface.openPanel();
                    }
                  }}
                  className="bg-menioo-green text-white px-6 py-3 rounded-lg hover:bg-menioo-green-dark transition-all font-semibold hover:scale-105 inline-flex items-center"
                >
                  🍪 Gérer mes cookies
                </button>
              </div>
            </section>

            {/* Sécurité */}
            <section className="mb-8">
              <div className="flex items-start">
                <Lock className="h-6 w-6 text-menioo-green mr-3 mt-1" />
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-menioo-navy mb-4">9. Sécurité des données</h2>
                  <p className="text-gray-700 mb-3">
                    Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données personnelles :
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2">
                    <li>Chiffrement des données en transit (HTTPS/TLS) et au repos</li>
                    <li>Authentification sécurisée via Firebase Authentication</li>
                    <li>Règles de sécurité Firebase (Firestore Security Rules) pour le contrôle d'accès aux données</li>
                    <li>Gestion des permissions par rôle au sein des organisations</li>
                    <li>Mots de passe chiffrés et jamais stockés en clair</li>
                    <li>Accès restreint aux données de production</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* IA */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-menioo-navy mb-4">10. Utilisation de l'intelligence artificielle</h2>
              <p className="text-gray-700 mb-3">Menioo utilise des technologies d'intelligence artificielle pour :</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>L'extraction automatique de recettes à partir de vidéos TikTok et de pages web</li>
                <li>La suggestion de recettes personnalisées (fonctionnalité premium)</li>
                <li>La catégorisation et le classement des recettes</li>
              </ul>
              <p className="text-gray-700">
                Ces traitements automatisés ne produisent pas d'effets juridiques significatifs à votre égard. Vous pouvez à tout moment contester un résultat généré par l'IA et demander une intervention humaine en nous contactant.
              </p>
            </section>

            {/* Modifications */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-menioo-navy mb-4">11. Modifications de cette politique</h2>
              <p className="text-gray-700">
                Nous nous réservons le droit de modifier cette politique de confidentialité à tout moment. En cas de modification substantielle, nous vous en informerons par email ou via une notification dans l'application au moins 30 jours avant l'entrée en vigueur des modifications. La date de dernière mise à jour est indiquée en haut de ce document.
              </p>
            </section>

            {/* Contact */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-menioo-navy mb-4">12. Contact</h2>
              <p className="text-gray-700 mb-3">
                Pour toute question relative à cette politique de confidentialité ou à vos données personnelles, vous pouvez nous contacter :
              </p>
              <ul className="list-none text-gray-700 space-y-2">
                <li><strong>Par email :</strong> <a href="mailto:alex@menioo.com" className="text-menioo-green hover:underline">alex@menioo.com</a></li>
                <li><strong>Site web :</strong> <a href="https://menioo.com" className="text-menioo-green hover:underline">https://menioo.com</a></li>
              </ul>
            </section>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200 flex justify-between items-center flex-wrap gap-4">
            <Link 
              to="/" 
              className="inline-flex items-center text-menioo-green hover:text-menioo-green-dark transition-colors font-medium"
            >
              <ChefHat className="mr-2 h-5 w-5" />
              Retour à l'accueil
            </Link>
            <Link 
              to="/terms" 
              className="text-gray-600 hover:text-menioo-navy transition-colors"
            >
              Voir les CGU →
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-menioo-navy text-white py-8 mt-12">
        <div className="container mx-auto px-6 md:px-12 text-center">
          <p className="text-gray-400">© {new Date().getFullYear()} Menioo. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
}
